import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plug, Package, Plus, ArrowLeft } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Toast from '../components/ui/Toast';
import ConnectorOption from '../components/integrations/ConnectorOption';
import ConnectorSetupForm from '../components/integrations/ConnectorSetupForm';
import WooCommerceConnect from '../components/integrations/WooCommerceConnect';
import InstalledConnectorCard from '../components/integrations/InstalledConnectorCard';
import { api } from '../services/api';
import { ConnectorCatalogEntry, Connection, WooCommerceSyncStatus } from '../types';

type ViewType = 'list' | 'select-connector' | 'setup';

const POLL_INTERVAL = 3000;
const MAX_POLL_COUNT = 60;

/** The one connector that also feeds the catalog pipeline, which is a separate seam with its own state. */
const CATALOG_SOURCE_CONNECTOR = 'woocommerce';

/**
 * Install, configure and remove the vendors this tenant talks to.
 *
 * The list of what can be installed comes from `GET /api/connectors` — the backend's capability
 * packs decide which vendors exist, what each asks for at setup, and which capabilities installing
 * one unlocks. Nothing about a specific vendor is hardcoded here beyond its icon, so a connector
 * added to a pack appears without a change to this page.
 */
const IntegrationsPage: React.FC = () => {
  const [view, setView] = useState<ViewType>('list');
  const [connectors, setConnectors] = useState<ConnectorCatalogEntry[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selected, setSelected] = useState<ConnectorCatalogEntry | null>(null);
  const [setupMode, setSetupMode] = useState<'install' | 'reconfigure'>('install');
  const [syncStatus, setSyncStatus] = useState<WooCommerceSyncStatus | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [verifying, setVerifying] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [preferManualSetup, setPreferManualSetup] = useState(false);
  const [searchParams] = useSearchParams();

  // Sent by the WordPress plugin, which knows its own address and should not make anyone retype it.
  const storeUrlFromPlugin = searchParams.get('store') ?? '';

  /**
   * The guided flow is the default for WooCommerce, and only for a NEW connection.
   *
   * Reconfiguring keeps the form: it exists to change one stored field — a rotated key, a moved
   * domain — and sending somebody back through their store's approval screen to do that would issue
   * a second credential pair for a store that already has one.
   */
  const showGuidedWooConnect =
    selected?.id === CATALOG_SOURCE_CONNECTOR && setupMode === 'install' && !preferManualSetup;

  const pollCountRef = useRef(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const loadCatalog = useCallback(async () => {
    const [catalog, installed] = await Promise.all([api.listConnectors(), api.listConnections()]);
    setConnectors(catalog.connectors);
    setConnections(installed.connections);
    return installed.connections;
  }, []);

  // Catalog-sync state is a different endpoint on a different seam, and its absence is normal — a
  // tenant can have the connection without ever having configured sync.
  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await api.getWooCommerceStatus();
      setSyncStatus(status);
      return status;
    } catch {
      setSyncStatus(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const installed = await loadCatalog();
        if (installed.some(connection => connection.connectorId === CATALOG_SOURCE_CONNECTOR)) {
          await loadSyncStatus();
        }
      } catch (error: any) {
        setToast({ message: error?.message || 'Failed to load integrations', type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [loadCatalog, loadSyncStatus]);

  /**
   * Arriving from the WordPress plugin, which links here with the store it wants connected.
   *
   * Only ever opens the setup screen — the merchant still presses the button and still approves on
   * their own store. A link that started an authorisation on its own would mean any page could
   * begin one on a merchant's behalf.
   *
   * Ignored once the connector is already installed: the honest answer to "connect this store" when
   * it IS connected is the list showing so, not a form to connect it again.
   */
  useEffect(() => {
    if (isLoading || searchParams.get('connect') !== CATALOG_SOURCE_CONNECTOR) return;
    if (connections.some(connection => connection.connectorId === CATALOG_SOURCE_CONNECTOR)) return;

    const connector = connectors.find(entry => entry.id === CATALOG_SOURCE_CONNECTOR);
    if (!connector) return;

    setSelected(connector);
    setSetupMode('install');
    setPreferManualSetup(false);
    setView('setup');
  }, [isLoading, connectors, connections, searchParams]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const startPolling = useCallback(() => {
    pollCountRef.current = 0;
    const poll = async () => {
      pollCountRef.current++;
      const status = await loadSyncStatus();
      if (!status || status.lastSyncStatus !== 'running' || pollCountRef.current >= MAX_POLL_COUNT) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsSyncing(false);
        if (status?.lastSyncStatus === 'success') {
          setToast({ message: 'Catalog sync completed', type: 'success' });
        } else if (status?.lastSyncStatus === 'error') {
          setToast({ message: `Sync failed: ${status.lastSyncError}`, type: 'error' });
        }
      }
    };
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL);
  }, [loadSyncStatus]);

  /**
   * Field-level messages the backend returned, so a rejected setup highlights the offending input
   * rather than showing one opaque banner.
   */
  const readFieldErrors = (error: any): Record<string, string> => {
    const issues = error?.params?.issues as { path: string; message: string }[] | undefined;
    if (!issues) return {};
    const mapped: Record<string, string> = {};
    for (const issue of issues) mapped[issue.path] = issue.message;
    return mapped;
  };

  /**
   * WooCommerce credentials still drive the catalog pipeline through its own configuration, so a
   * setup writes both. TRANSITIONAL: once catalog-sync reads the connection, this second write and
   * the credentials it carries should be deleted.
   */
  const mirrorToCatalogSync = async (config: Record<string, string>, secrets: Record<string, string>) => {
    if (!secrets.consumerKey || !secrets.consumerSecret) return;
    await api.updateWooCommerceConfig({
      platform: 'woocommerce',
      enabled: true,
      baseUrl: config.baseUrl,
      auth: { consumerKey: secrets.consumerKey, consumerSecret: secrets.consumerSecret },
      sync: {
        mode: 'manual',
        entities: ['products', 'categories'],
        perPage: 100,
        includeDrafts: false,
        includeVariations: false,
        fullResyncOnNextRun: false,
      },
    } as any);
  };

  const handleSubmitSetup = async (values: {
    config: Record<string, string>;
    secrets: Record<string, string>;
  }) => {
    if (!selected) return;
    setIsSaving(true);
    setFieldErrors({});
    try {
      if (setupMode === 'install') {
        await api.installConnector({
          connectorId: selected.id,
          config: values.config,
          secrets: values.secrets,
        });
      } else {
        await api.reconfigureConnection(selected.id, {
          config: values.config,
          secrets: values.secrets,
        });
      }

      if (selected.id === CATALOG_SOURCE_CONNECTOR) {
        await mirrorToCatalogSync(values.config, values.secrets);
      }

      await loadCatalog();
      if (selected.id === CATALOG_SOURCE_CONNECTOR) await loadSyncStatus();

      setToast({
        message: setupMode === 'install' ? `${selected.label} installed` : `${selected.label} updated`,
        type: 'success',
      });
      goToList();
    } catch (error: any) {
      setFieldErrors(readFieldErrors(error));
      setToast({ message: error?.userMessage || error?.message || 'Setup failed', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleUninstall = async (connection: Connection, label: string) => {
    const confirmed = window.confirm(
      `Remove ${label}?\n\nIts stored credentials are deleted, and anything that depended on it stops working until another integration provides it.`
    );
    if (!confirmed) return;

    try {
      await api.uninstallConnector(connection.connectorId);
      await loadCatalog();
      if (connection.connectorId === CATALOG_SOURCE_CONNECTOR) setSyncStatus(null);
      setToast({ message: `${label} removed`, type: 'success' });
    } catch (error: any) {
      setToast({ message: error?.userMessage || error?.message || 'Failed to remove', type: 'error' });
    }
  };

  /** The provider's own wording for the first failed check — far more actionable than a check name. */
  const firstProblem = (result: { checks: { passed: boolean; detail?: string }[] }): string | undefined =>
    result.checks.find(check => !check.passed && check.detail)?.detail

  const handleVerify = async (connectorId: string, label: string) => {
    setVerifying(connectorId);
    try {
      const result = await api.verifyConnection(connectorId);
      const failed = result.checks.filter(check => !check.passed).map(check => check.name);
      setToast({
        message: result.verified
          ? `${label} is connected and its credentials work`
          : `${label}: ${firstProblem(result) ?? failed.join(', ')}`,
        type: result.verified ? 'success' : 'error',
      });
      await loadCatalog();
    } catch (error: any) {
      setToast({ message: error?.userMessage || error?.message || 'Check failed', type: 'error' });
    } finally {
      setVerifying(null);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await api.triggerWooCommerceSync();
      setSyncStatus(prev => (prev ? { ...prev, lastSyncStatus: 'running' } : prev));
      startPolling();
    } catch (error: any) {
      setIsSyncing(false);
      setToast({ message: error?.message || 'Failed to trigger sync', type: 'error' });
    }
  };

  const goToSelectConnector = () => {
    setSelected(null);
    setFieldErrors({});
    setView('select-connector');
  };

  const goToList = () => {
    setSelected(null);
    setFieldErrors({});
    setView('list');
  };

  const startInstall = (connector: ConnectorCatalogEntry) => {
    setSelected(connector);
    setSetupMode('install');
    setFieldErrors({});
    setPreferManualSetup(false);
    setView('setup');
  };

  const startReconfigure = (connectorId: string) => {
    const connector = connectors.find(entry => entry.id === connectorId);
    if (!connector) return;
    setSelected(connector);
    setSetupMode('reconfigure');
    setFieldErrors({});
    setView('setup');
  };

  const labelFor = (connectorId: string): string =>
    connectors.find(entry => entry.id === connectorId)?.label ?? connectorId;

  const renderInstalled = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      );
    }

    if (connections.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold dark:text-white text-gray-900 mb-2">
            No integrations installed
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            Install one to let the assistant read your store — orders, products and more.
          </p>
          <button onClick={goToSelectConnector} className="text-primary font-medium hover:underline">
            Install your first integration →
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {connections.map(connection => (
          <InstalledConnectorCard
            key={connection.connectorId}
            connection={connection}
            label={labelFor(connection.connectorId)}
            syncStatus={connection.connectorId === CATALOG_SOURCE_CONNECTOR ? syncStatus : undefined}
            onSync={connection.connectorId === CATALOG_SOURCE_CONNECTOR ? handleSync : undefined}
            onConfigure={() => startReconfigure(connection.connectorId)}
            onVerify={() => handleVerify(connection.connectorId, labelFor(connection.connectorId))}
            onUninstall={() => handleUninstall(connection, labelFor(connection.connectorId))}
            isSyncing={isSyncing}
            isVerifying={verifying === connection.connectorId}
          />
        ))}
      </div>
    );
  };

  const renderCatalog = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold dark:text-white text-gray-900 mb-2">
          Choose an integration
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Everything available on your plan. Installing one enables the capabilities it provides.
        </p>
      </div>

      {connectors.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No integrations are available for this tenant.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {connectors.map(connector => (
            <ConnectorOption
              key={connector.id}
              connector={connector}
              onSelect={() => startInstall(connector)}
            />
          ))}
        </div>
      )}
    </div>
  );

  const titles: Record<ViewType, { title: string; subtitle: string }> = {
    list: { title: 'Integrations', subtitle: 'Connect the platforms your store already runs on' },
    'select-connector': { title: 'New Integration', subtitle: 'Select a platform to install' },
    setup: {
      title: setupMode === 'install' ? 'Install Integration' : 'Configure Integration',
      subtitle:
        setupMode === 'install'
          ? 'Enter the details this platform needs'
          : 'Change what this integration was set up with',
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {view !== 'list' && (
            <button
              onClick={view === 'setup' && setupMode === 'install' ? goToSelectConnector : goToList}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          )}
          <div className="p-2 rounded-lg bg-primary/10">
            <Plug className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold dark:text-white text-gray-900">{titles[view].title}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">{titles[view].subtitle}</p>
          </div>
        </div>

        {view === 'list' && (
          <Button onClick={goToSelectConnector}>
            <Plus className="w-4 h-4 mr-2" />
            New Integration
          </Button>
        )}
      </div>

      {view === 'list' && <Card>{renderInstalled()}</Card>}
      {view === 'select-connector' && <Card>{renderCatalog()}</Card>}
      {view === 'setup' && selected && showGuidedWooConnect && (
        <Card className="max-w-2xl">
          <WooCommerceConnect
            defaultStoreUrl={storeUrlFromPlugin}
            onUseManualSetup={() => setPreferManualSetup(true)}
          />
        </Card>
      )}
      {view === 'setup' && selected && !showGuidedWooConnect && (
        <Card className="max-w-2xl">
          <ConnectorSetupForm
            connector={selected}
            mode={setupMode}
            currentConfig={
              setupMode === 'reconfigure'
                ? connections.find(item => item.connectorId === selected.id)?.config
                : undefined
            }
            onSubmit={handleSubmitSetup}
            isSaving={isSaving}
            fieldErrors={fieldErrors}
          />
        </Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default IntegrationsPage;
